/**
 * The waitlist without an account (GH-871b, DEC-052).
 *
 * Owner, 2026-10-03: no code → *"Waitlist without account"*: a phone or an email and a place in
 * line; no account is created until the person is admitted or invited. So: one field ("Your phone
 * or email"), the code the server sends to it, and the answer —
 *
 *   - `waiting`: "You're #N in line", and the server's "Over N waiting" where it gives one;
 *   - `admitted`: "Your invite is coming" — the admission code is on its way to that contact;
 *   - `can_sign_in`: "You already have an account" — and the way back to signing in.
 *
 * The start route answers the same whoever the contact is, so this screen cannot be used to learn
 * who is on the list. The text-message notice is shown beside the field before anything is sent:
 * the server records it with the SMS consent (`WAITLIST_SMS_DISCLOSURE_VERSION`).
 *
 * A membership surface: the place in line is DEC-051's scarcity exception. It is drawn on
 * `/waitlist` and inside the sign-in sheet, never on an event, organization or give screen — the
 * check-in page links to `/waitlist` instead (`InviteRequiredStep`).
 */
import { useState } from "react";
import { ACCESS_COPY, MEMBERSHIP_COPY, type WaitlistConfirmResponse } from "@givehere/contracts";
import { ApiError, confirmAccountlessWaitlist, startAccountlessWaitlist } from "./api.js";
import { grouped } from "./share.js";

type Channel = "sms" | "email";

type Step =
  | { kind: "contact" }
  | { kind: "code"; channel: Channel; address: string; verificationId: string; hint: string | null }
  | { kind: "done"; answer: WaitlistConfirmResponse };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PHONE_DIGITS = 7;

/** An address with "@" is an inbox; anything else is a phone. */
export const channelOf = (contact: string): Channel => (contact.includes("@") ? "email" : "sms");

export const isUsableContact = (contact: string): boolean => {
  const trimmed = contact.trim();
  return channelOf(trimmed) === "email"
    ? EMAIL.test(trimmed)
    : trimmed.replace(/\D/g, "").length >= MIN_PHONE_DIGITS;
};

const failure = (caught: unknown, fallback: string): string =>
  caught instanceof ApiError ? caught.message : fallback;

export function AccountlessWaitlist({
  initialContact = "",
  heading = null,
  headingId,
  onSignIn,
}: {
  /** The phone the person just typed into the sign-in sheet, so they need not type it twice. */
  initialContact?: string;
  /** A title for the first step, where the host has none of its own (the sheet). */
  heading?: string | null;
  /** The id the host's dialog is labelled by; it moves with each step's heading. */
  headingId?: string | undefined;
  /** "You already have an account": back to signing in. Absent: no button. */
  onSignIn?: () => void;
}) {
  const [contact, setContact] = useState(initialContact);
  const [code, setCode] = useState("");
  const [step, setStep] = useState<Step>({ kind: "contact" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const channel = channelOf(contact.trim());

  const send = () => {
    const address = contact.trim();
    if (!isUsableContact(address)) return;
    setError(null);
    setBusy(true);
    startAccountlessWaitlist({ channel, address })
      .then((started) =>
        setStep({
          kind: "code",
          channel,
          address,
          verificationId: started.verificationId,
          hint: started.developmentCode,
        }),
      )
      .catch((caught: unknown) => setError(failure(caught, "Could not send a code.")))
      .finally(() => setBusy(false));
  };

  const confirm = (current: Extract<Step, { kind: "code" }>) => {
    setError(null);
    setBusy(true);
    confirmAccountlessWaitlist({
      verificationId: current.verificationId,
      code,
      channel: current.channel,
      address: current.address,
    })
      .then((answer) => setStep({ kind: "done", answer }))
      .catch((caught: unknown) => setError(failure(caught, "That code is not correct.")))
      .finally(() => setBusy(false));
  };

  const errorLine = error && (
    <p className="notice notice--error" role="alert">
      {error}
    </p>
  );

  if (step.kind === "done") {
    const { answer } = step;
    return (
      <div className="waitlist-flow" role="status">
        <h2 id={headingId} className="donor__title">
          {answer.state === "waiting" && answer.position !== null
            ? MEMBERSHIP_COPY.waitlistPosition(grouped(answer.position))
            : answer.state === "admitted"
              ? ACCESS_COPY.waitlistAdmitted
              : ACCESS_COPY.waitlistCanSignIn}
        </h2>
        {answer.state === "waiting" && answer.waitingDisplay && (
          <p className="donor__where">{answer.waitingDisplay}</p>
        )}
        {answer.state !== "can_sign_in" && (
          <p className="donor__where">{ACCESS_COPY.waitlistNoAccount}</p>
        )}
        {answer.state === "can_sign_in" && onSignIn && (
          <button type="button" className="pay" onClick={onSignIn}>
            Sign in
          </button>
        )}
      </div>
    );
  }

  if (step.kind === "code") {
    return (
      <div className="waitlist-flow">
        <h2 id={headingId} className="donor__title">
          {ACCESS_COPY.enterCodeWeSent}
        </h2>
        {step.hint && (
          <p className="notice">
            Test mode — your code is <strong>{step.hint}</strong>
          </p>
        )}
        <label className="field">
          <span className="visually-hidden">Verification code</span>
          <input
            inputMode="numeric"
            maxLength={10}
            autoComplete="one-time-code"
            value={code}
            onChange={(changed) => setCode(changed.target.value.replace(/\D/g, ""))}
          />
        </label>
        <button
          type="button"
          className="pay"
          disabled={busy || code.length < 4}
          onClick={() => confirm(step)}
        >
          {busy ? "Checking…" : "Confirm"}
        </button>
        {errorLine}
      </div>
    );
  }

  return (
    <div className="waitlist-flow">
      {heading && (
        <h2 id={headingId} className="donor__title">
          {heading}
        </h2>
      )}
      <label className="field">
        <span>{ACCESS_COPY.waitlistContact}</span>
        <input
          type="text"
          inputMode={channel === "email" ? "email" : "tel"}
          autoComplete="off"
          spellCheck={false}
          maxLength={254}
          value={contact}
          onChange={(changed) => setContact(changed.target.value)}
        />
      </label>
      <p className="donor__where">
        {channel === "email" ? ACCESS_COPY.waitlistEmailNotice : ACCESS_COPY.waitlistSmsNotice}
      </p>
      <p className="donor__where">{ACCESS_COPY.waitlistNoAccount}</p>
      <button
        type="button"
        className="pay"
        disabled={busy || !isUsableContact(contact)}
        onClick={send}
      >
        {busy ? "Sending…" : ACCESS_COPY.sendCode}
      </button>
      {errorLine}
    </div>
  );
}
